import React, { useRef } from 'react';

import styled from '@emotion/styled';

import CompanyLogo from '../../../components/CompanyLogo/CompanyLogo';
import CloseIcon from '../../../components/Icons/CloseIcon';
import { NavBoxItem } from '../../../components/Link/Link';
import useOnLogout from '../../../hooks/auth/useOnLogout';
import { navConfig } from './navConfig';
import { ModalProvider } from '../../../context/ModalContext';
import ModalTriggerContainer from '../../../components/Modals/ModalTriggerContainer';
import { Modal } from '../../../components/Modals/Modal';
import ProfileModal from '../../../components/Modals/ModalsActions/ProfileModal';
import GuestPromptModal from '../../../components/Modals/ModalsActions/GuestPromptModal';

interface IHamburgerMenu {
  open: boolean;
  toggleNav: (...arg: any) => void;
  guest?: boolean;
}

const MobileNav: React.FC<IHamburgerMenu> = ({ open, toggleNav, guest }) => {
  const { logoutUser } = useOnLogout();
  const bgRef = useRef(null);
  const handleNavMenuItemClick = (_: any) => {
    toggleNav();
  };

  const handleBgClick = (e: any) => {
    if (bgRef && bgRef.current === e.target) {
      toggleNav();
    }
  };

  const handleHelpClick = () => {
    window.open('https://locumspace.co/contact-us', '_blank');
  };

  return (
    <MainContainer
      ref={bgRef}
      className={open ? '' : 'close'}
      onClick={handleBgClick}
    >
      <Container className={open ? '' : 'close'}>
        <Header>
          <CompanyLogo />
          <CloseIconContainer onClick={toggleNav}>
            <CloseIcon />
          </CloseIconContainer>
        </Header>

        <NavBtnSection onClick={handleNavMenuItemClick}>
          <ModalProvider>
            <ModalTriggerContainer>
              <ViewProfile>View Profile</ViewProfile>
            </ModalTriggerContainer>
            <Modal>{guest ? <GuestPromptModal /> : <ProfileModal />}</Modal>
          </ModalProvider>
          {navConfig.map((nav) => (
            <NavBoxItem path={nav.path} key={nav.title} icon={nav.icon}>
              {nav.title}
            </NavBoxItem>
          ))}
          {guest ? (
            <Bottom>
              <HelpBox onClick={handleHelpClick}>Feedback</HelpBox>
              <LogoutBox>Guest</LogoutBox>
            </Bottom>
          ) : (
            <Bottom>
              <HelpBox onClick={handleHelpClick}>Feedback</HelpBox>
              <LogoutBox onClick={() => logoutUser()}>Logout</LogoutBox>
            </Bottom>
          )}
        </NavBtnSection>
      </Container>
    </MainContainer>
  );
};

export default MobileNav;

const MainContainer = styled.div`
  display: None;
  height: 100vh;
  position: fixed;
  background: rgba(17, 23, 29, 0.35);

  @media (max-width: 884px) {
    width: 100vw;
    display: flex;
    margin-left: auto;
    z-index: 2000;

    &.open {
      transform: translateY(0);
    }

    &.close {
      transform: translateY(-100%);
      opacity: 0;
      transition: transform 0s ease-in-out 0.3s, opacity 0.3s ease-in-out 0s;
    }
  }
`;

const Container = styled.nav`
  display: flex;
  padding: 24px 24px;
  background-color: white;
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;

  flex-direction: column;
  gap: 3.8rem;
  position: absolute;
  width: 100vw;
  height: fit-content;
  top: 0;

  gap: 24px;

  &.open {
    transform: translateY(0);
  }

  &.close {
    transform: translateY(-100%);
  }
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseIconContainer = styled.div`
  display: grid;
  place-items: center;
  cursor: pointer;

  & > * {
    transition: transform 0.3s ease-in-out;
    &:hover {
      transform: scale(110%);
    }
  }
`;

const NavBtnSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 7px;

  @media (max-width: 884px) {
    gap: 32px;
  }
`;

const LogoutBox = styled.div`
  margin-top: auto;
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 1rem;
  font-weight: 400;
  display: flex;
  gap: 1.25rem;
  cursor: pointer;
  align-items: center;

  &:hover {
    color: ${({ theme }) => theme.palette.stateColorRed};
  }
`;

const ViewProfile = styled.div`
  color: ${({ theme }) => theme.palette.greyGrey2};
  font-size: 1rem;
  font-weight: 400;

  &:hover {
    color: ${({ theme }) => theme.palette.greyGrey1};
  }
`;

const Bottom = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.palette.greyGrey3};
`;

const HelpBox = styled.div`
  color: ${({ theme }) => theme.palette.greyGrey2};
  font-size: 1rem;
  font-weight: 400;
  display: flex;
  gap: 1.25rem;
  cursor: pointer;
  align-items: center;

  &:hover {
    color: ${({ theme }) => theme.palette.greyGrey1};
  }
`;
